import { useState } from "react"
import { useDispatch } from "react-redux"
import { toast } from "react-toastify"
import { CellContext } from "@tanstack/react-table"
import styled from "styled-components"

import { useLazyGetDevicesQuery } from "store/api/devicesApi"
import { useLazyGetPerformanceTestReportQuery } from "store/api/performanceTestApi"
import { showPerformanceTestReportModal } from "store/modalSlise"
import { setPerformanceTestReport } from "store/tempDataSlise"
import { ApiTypes, UITypes } from "types"
import { ReactComponent as IconTableCross } from "assets/svg/icon-table-cross.svg"

import PerfTestStatusChangeModal from "modals/PerfTestStatusChangeModal/PerfTestStatusChangeModal"
import { PerformanceTestStatusBadge } from "components/common/PerformanceTestStatusBadge"

const Container = styled.div`
  display: inline-flex;
  align-items: center;
  position: relative;

  &:hover {
    div {
      display: block;
    }
  }
`

const ClearIcon = styled.div`
  height: 16px;
  margin-left: 4px;
  display: none;
  svg {
    stroke: #64748b;
  }

  &:hover {
    svg {
      stroke: #0f172a;
    }
  }
`

interface Props {
  cell: CellContext<UITypes.TableBodyRow, UITypes.TableBodyCell>
}

export const StatusOfPerformanceTest = ({ cell: { getValue, row } }: Props) => {
  const type = getValue().value ? String(getValue().value) : ""
  const currentItem = row.original.rowSettings.item
  const dispatch = useDispatch()
  const [showStatusChangeModal, setShowStatusChangeModal] = useState(false)
  const [triggerGetPerformanceTestReport] = useLazyGetPerformanceTestReportQuery()
  const [triggerGetDevice] = useLazyGetDevicesQuery()

  if (!type) return null

  const handleTableRowClick = async (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    if (!["passed", "issue"].includes(String(type)) || !currentItem) {
      return
    }

    e.stopPropagation()

    let device: ApiTypes.Model.Device | undefined

    if (currentItem.perftest && currentItem?.device) {
      const deviceData = await triggerGetDevice({ sn: currentItem?.device?.sn })

      if (deviceData.error) {
        toast.error("Something went wrong, please try again")
        return
      }

      device = deviceData.data?.data[0]
    }

    const res = await triggerGetPerformanceTestReport(currentItem?.perftest.perftest_result_id)
    const currentDevice = device || currentItem?.device || currentItem

    if (res.data) {
      dispatch(
        setPerformanceTestReport({
          ...res?.data,
          customer: currentDevice?.homeowner_name,
          zip_code: currentDevice?.zip?.code,
          where_installed_name: currentDevice.name || currentDevice?.residence_type?.name,
          timeZone: currentDevice?.time_zone_text || "",
        }),
      )
      dispatch(showPerformanceTestReportModal(true))
    }
  }

  const handleStatusChangeClick = async (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation()
    e.preventDefault()
    setShowStatusChangeModal(true)
  }

  const onCancel = () => {
    setShowStatusChangeModal(false)
  }

  return (
    <>
      <Container onClick={handleTableRowClick}>
        <PerformanceTestStatusBadge status={type} onClick={handleTableRowClick} />
        {type === "issue" && (
          <ClearIcon onClick={handleStatusChangeClick}>
            <IconTableCross />
          </ClearIcon>
        )}
      </Container>
      {showStatusChangeModal && (
        <PerfTestStatusChangeModal perfTestResultId={currentItem?.perftest.perftest_result_id} onCancel={onCancel} />
      )}
    </>
  )
}
